import React, { useEffect, useRef } from "react";
import Button, { ButtonColor } from "components/button";
import Typography from "components/typography";
import Icon, { IconName } from "components/icon";
import { cn } from "_helper/cn";

interface ConfirmationModalProps {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
  testId?: string;
  icon?: "info" | "danger" | "warning" | "success";
  iconColor?: string;
  children?: React.ReactNode;
  loading?: boolean;
  processingLabel?: string;
  hideCancelButton?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  open,
  testId = "confirmation-modal",
  icon = "info",
  iconColor,
  children,
  loading,
  processingLabel = "Processing...",
  hideCancelButton = false,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) {
        dialog.showModal();
        document.body.style.overflow = "hidden";
      }
    } else {
      if (dialog.open) {
        dialog.close();
        document.body.style.overflow = "";
      }
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onCancel();
    }
  };

  const getIconName = (): IconName => {
    switch (icon) {
      case "danger":
        return "Warning";
      case "warning":
        return "WarningCircle";
      case "success":
        return "SealCheck";
      case "info":
      default:
        return "WarningCircle";
    }
  };

  const getIconColor = () => {
    if (iconColor) return iconColor;
    switch (icon) {
      case "danger":
        return "text-[#E74C3C]";
      case "warning":
        return "text-[#F1C40F]";
      case "success":
        return "";
      case "info":
      default:
        return "text-[#1E40AF]"; // Dark blue for info as seen in Figma
    }
  };

  const getConfirmButtonColor = (): ButtonColor => {
    if (hideCancelButton) {
      return "tertiary";
    }
    if (icon === "danger") {
      return "error";
    }
    return "primary";
  };

  const confirmButtonColor = getConfirmButtonColor();

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-white p-0 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop:bg-black/40 max-w-[380px] w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      onClick={handleBackdropClick}
      onClose={onCancel}
      data-testid={testId}
    >
      <div className="flex flex-col w-full">
        {/* Main Content */}
        <div className="flex flex-col items-center pt-10 px-8 pb-8 text-center">
          {/* Icon */}
          <div 
            className={cn("mb-5", getIconColor())}
            data-testid={`${testId}-icon`}
          >
            <Icon name={getIconName()} size={64} />
          </div>

          {/* Title */}
          <Typography variant="heading5" className="mb-3 font-semibold text-[#1E293B]">
            {title}
          </Typography>

          <div className="text-[14px] leading-relaxed text-[#64748B] px-2 mb-4">
            {message}
          </div>

          {children && (
            <div className="w-full">
              {children}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="w-full px-6 py-5 border-t border-[#E2E8F0] bg-white flex gap-3">
          {!hideCancelButton && (
            <Button
              type="button"
              variant="outline"
              color="tertiary"
              size="large"
              isFullSize
              onClick={onCancel}
              className="rounded-xl border-[#CBD5E1] font-medium text-[#475569] hover:bg-[#F8FAFC] h-[48px]"
              data-testid={`${testId}-cancel`}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            type="button"
            variant={hideCancelButton ? "outline" : "fill"}
            color={confirmButtonColor}
            size="large"
            isFullSize
            onClick={onConfirm}
            disabled={loading}
            iconLeft={loading ? <Icon name="refresh" size={18} className="animate-spin" /> : undefined}
            className={cn(
              "rounded-xl font-medium h-[48px]",
              hideCancelButton
                ? "border border-[#2F394A] text-[#2F394A] bg-white hover:bg-[#F8FAFC] active:scale-[0.98] transition-transform"
                : undefined,
              icon === "danger"
                ? "bg-[#E74C3C] hover:bg-[#C0392B]"
                : undefined
            )}
            data-testid={`${testId}-confirm`}
          >
            {loading ? processingLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default ConfirmationModal;
